/**
 * RegisterPage — iter175 hardened.
 *
 * Anti-abuse layers on top of the base form:
 *   1. Cloudflare Turnstile widget (explicit render)
 *   2. Hidden honeypot input (`website`) — real users never fill it
 *   3. Form-fill timer sent server-side (min 2s to submit)
 *   4. Stronger password policy with live inline hints
 *   5. Post-signup screen prompts email verification
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import DotboxLogo from '@/components/common/DotboxLogo';
import {
  Eye, EyeOff, Loader2, Check, Linkedin, Twitter, Facebook, MailCheck, ShieldCheck,
} from 'lucide-react';

const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
// iter175 — Turnstile production keys are hostname-locked in the Cloudflare
// dashboard. On the *.preview.emergentagent.com sandbox the production key
// silently fails, so fall back to Cloudflare's well-known always-pass dev
// key for any non-production hostname. Production still uses the real key.
const PROD_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';
const ALWAYS_PASS_DEV_KEY = '1x00000000000000000000AA';
const isProdHostname = (() => {
  if (typeof window === 'undefined') return true;
  const h = window.location.hostname || '';
  return h === 'dotbox.ai' || h.endsWith('.dotbox.ai');
})();
const SITE_KEY = isProdHostname ? PROD_SITE_KEY : ALWAYS_PASS_DEV_KEY;

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const invitedEmail = searchParams.get('email') || '';
  const inviteToken = searchParams.get('invite');
  useEffect(() => {
    if (inviteToken) {
      try { localStorage.setItem('dotbox.pendingInviteToken', inviteToken); } catch { /* ignore */ }
    }
  }, [inviteToken]);

  const [formData, setFormData] = useState({
    fullName: '', email: invitedEmail, password: '', confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [linkedinLoading, setLinkedinLoading] = useState(false);
  const [twitterLoading, setTwitterLoading] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [postSignup, setPostSignup] = useState(null); // {email, name}
  // iter175 — anti-abuse fields
  const [honeypot, setHoneypot] = useState('');
  const formStartRef = useRef(Date.now());
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState('');
  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  // iter175 follow-up — Load Turnstile api.js + render widget once.
  // The previous version called `window.turnstile.ready(...)` as soon as the
  // script's `load` event fired, but Cloudflare's api.js occasionally sets the
  // global before its internal state machine is initialised, producing a
  // "turnstile.ready() would break if called before api.js loaded" warning
  // and no widget in headless/preview environments. We now poll until
  // `window.turnstile.render` is actually a function — the strongest signal
  // that api.js is fully wired — before calling ready().
  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;

    const renderWidget = () => {
      if (
        cancelled ||
        turnstileWidgetIdRef.current !== null ||
        !turnstileContainerRef.current ||
        !window.turnstile ||
        typeof window.turnstile.render !== 'function'
      ) {
        return;
      }
      try {
        turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
          sitekey: SITE_KEY,
          theme: 'dark',
          callback: (token) => {
            setTurnstileError('');
            setTurnstileToken(token);
          },
          'expired-callback': () => {
            setTurnstileToken('');
            setTurnstileError('Verification expired. Please complete it again.');
          },
          'error-callback': () => {
            setTurnstileToken('');
            setTurnstileError('Verification could not load. Please refresh.');
          },
        });
      } catch (err) {
        console.warn('Turnstile render failed:', err);
        setTurnstileError('Verification could not load. Please refresh.');
      }
    };

    const waitForApiAndRender = () => {
      if (cancelled) return;
      if (window.turnstile && typeof window.turnstile.render === 'function') {
        try {
          window.turnstile.ready(renderWidget);
        } catch {
          // Some builds of api.js expose .render before .ready — call render directly.
          renderWidget();
        }
        return;
      }
      pollTimer = setTimeout(waitForApiAndRender, 100);
    };

    let script = document.querySelector(`script[src="${TURNSTILE_SCRIPT}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT;
      script.async = true;
      script.defer = true;
      script.addEventListener('error', () => {
        if (!cancelled) setTurnstileError('Unable to load bot verification.');
      });
      document.head.appendChild(script);
    }
    // Start polling immediately — resolves the same tick if api.js is cached.
    waitForApiAndRender();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      if (turnstileWidgetIdRef.current !== null && window.turnstile) {
        try { window.turnstile.remove(turnstileWidgetIdRef.current); } catch { /* ignore */ }
        turnstileWidgetIdRef.current = null;
      }
    };
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Live password strength hints
  const passwordChecks = {
    len:    formData.password.length >= 8,
    letter: /[A-Za-z]/.test(formData.password),
    digit:  /\d/.test(formData.password),
    match:  formData.password && formData.password === formData.confirmPassword,
  };

  const validateForm = () => {
    const { fullName, email, password, confirmPassword } = formData;
    if (!fullName || !email || !password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return false;
    }
    if (fullName.trim().length < 2) {
      toast.error('Full name must be at least 2 characters');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!passwordChecks.len) {
      toast.error('Password must be at least 8 characters');
      return false;
    }
    if (!passwordChecks.letter || !passwordChecks.digit) {
      toast.error('Password must include at least one letter and one number');
      return false;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    if (!turnstileToken) {
      setTurnstileError('Please complete the bot verification first.');
      return false;
    }
    return true;
  };

  const resetTurnstile = () => {
    setTurnstileToken('');
    if (turnstileWidgetIdRef.current !== null && window.turnstile) {
      try { window.turnstile.reset(turnstileWidgetIdRef.current); } catch { /* ignore */ }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const fillSeconds = (Date.now() - formStartRef.current) / 1000;

    setLoading(true);
    try {
      const result = await register({
        full_name: formData.fullName.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        cf_turnstile_token: turnstileToken,
        form_fill_seconds: fillSeconds,
        honeypot,
      });
      if (result.success) {
        // iter250 — the account is inert until the link is clicked: there is
        // no session to carry anywhere. A pending team invite was already
        // auto-accepted server-side at signup, so it survives the wait.
        setPostSignup({
          email: formData.email.trim().toLowerCase(),
          name: formData.fullName.trim(),
          devActivationUrl: result.dev_activation_url || null,
        });
      } else {
        toast.error(result.error || 'Registration failed');
        resetTurnstile();
      }
    } catch {
      toast.error('An unexpected error occurred');
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  if (postSignup) {
    return (
      <div className="min-h-screen bg-[#0A1024] constellation-bg flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <DotboxLogo className="mx-auto h-10" variant="dark" />
          </div>
          <Card
            data-testid="post-signup-verify-card"
          >
            <CardContent className="p-8 text-center">
              <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <MailCheck className="h-10 w-10 text-emerald-400" />
              </div>
              <h1 className="text-xl font-bold text-white mb-2">Check your inbox</h1>
              <p className="text-slate-400 text-sm mb-6">
                We just sent an activation link to <strong className="text-white">{postSignup.email}</strong>.
                Click it to activate your account — your 7-day trial starts from that moment,
                so nothing is ticking while you get to it.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-[#DC2626] hover:bg-[#B91C1C]"
                data-testid="post-signup-continue-btn"
              >
                Go to sign in
              </Button>

              {/* iter251 — preview environments bypass Turnstile, so the link
                  is echoed here: the flow can be walked end-to-end without
                  mailbox access. The backend refuses to emit this on the
                  production host. */}
              {postSignup.devActivationUrl && (
                <div
                  className="mt-5 rounded-md border border-dashed border-amber-700/60 bg-amber-950/20 p-3 text-left"
                  data-testid="post-signup-dev-activation"
                >
                  <p className="text-[10px] uppercase tracking-wider text-amber-400 font-semibold mb-1.5">
                    Preview mode — no inbox needed
                  </p>
                  <a
                    href={postSignup.devActivationUrl}
                    className="text-xs text-amber-200 hover:text-amber-100 underline break-all leading-relaxed"
                    data-testid="post-signup-dev-activation-link"
                  >
                    Activate this account now
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(postSignup.devActivationUrl);
                      toast.success('Activation link copied');
                    }}
                    className="mt-2 block text-[10px] text-amber-500/80 hover:text-amber-300 transition-colors"
                    data-testid="post-signup-dev-activation-copy"
                  >
                    Copy link
                  </button>
                </div>
              )}
              <p className="text-xs text-slate-500 mt-4">
                Didn&apos;t get the email? Check spam, or{' '}
                <Link
                  to={`/activate?email=${encodeURIComponent(postSignup.email)}`}
                  className="font-semibold text-red-400 hover:text-red-300"
                  data-testid="post-signup-resend-link"
                >
                  send it again
                </Link>
                .
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1024] constellation-bg relative flex items-center justify-center p-4 overflow-hidden">
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <DotboxLogo className="mx-auto mb-4 h-10" variant="dark" />
          <h1 className="text-2xl font-bold text-white">Get started for free</h1>
          <div className="flex items-center justify-center mt-3">
            <Badge className="bg-emerald-600/15 text-emerald-400 border border-emerald-600/40 px-3 py-1">
              <Check className="h-3 w-3 mr-1" />
              7-day free trial
            </Badge>
          </div>
        </div>

        <Card className="shadow-xl shadow-black/40 border">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl font-bold text-center text-white">Create Account</CardTitle>
            <CardDescription className="text-center text-slate-400">
              Start your free trial today — no credit card required
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* iter175 — honeypot. Positioned off-screen so real users never see it. */}
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute', left: '-9999px', top: 'auto',
                  width: '1px', height: '1px', overflow: 'hidden',
                }}
              >
                <label htmlFor="website">Leave this empty</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  data-testid="register-honeypot"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName" name="fullName" type="text"
                  placeholder="Enter your full name"
                  value={formData.fullName} onChange={handleChange}
                  disabled={loading} className="h-11"
                  data-testid="register-fullname-input" required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email" name="email" type="email"
                  placeholder="Enter your work email"
                  value={formData.email} onChange={handleChange}
                  disabled={loading} className="h-11"
                  data-testid="register-email-input" required
                />
                <p className="text-xs text-slate-500">
                  Disposable / throwaway emails aren't allowed.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password" name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a strong password"
                    value={formData.password} onChange={handleChange}
                    disabled={loading} className="h-11 pr-10"
                    data-testid="register-password-input" required
                  />
                  <button
                    type="button" tabIndex={-1}
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <ul className="text-[11px] text-slate-500 space-y-0.5 mt-1">
                  <li className={passwordChecks.len ? 'text-emerald-400' : ''}>
                    {passwordChecks.len ? '✓' : '•'} At least 8 characters
                  </li>
                  <li className={passwordChecks.letter ? 'text-emerald-400' : ''}>
                    {passwordChecks.letter ? '✓' : '•'} At least one letter
                  </li>
                  <li className={passwordChecks.digit ? 'text-emerald-400' : ''}>
                    {passwordChecks.digit ? '✓' : '•'} At least one number
                  </li>
                </ul>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword" name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm your password"
                    value={formData.confirmPassword} onChange={handleChange}
                    disabled={loading} className="h-11 pr-10"
                    data-testid="register-confirm-password-input" required
                  />
                  <button
                    type="button" tabIndex={-1}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-slate-300"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* iter175 — Turnstile bot check */}
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs uppercase tracking-wider flex items-center gap-1">
                  <ShieldCheck className="h-3 w-3 text-emerald-400" /> Bot check
                </Label>
                <div
                  ref={turnstileContainerRef}
                  className="min-h-[65px]"
                  data-testid="turnstile-widget"
                  data-cf-turnstile-container="true"
                />
                {turnstileError && (
                  <p className="text-xs text-rose-400" data-testid="register-turnstile-error">
                    {turnstileError}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-semibold shadow-lg shadow-red-900/30"
                disabled={loading}
                data-testid="register-submit-button"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating account...</>
                ) : ('Create Account')}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#1F2A4D]" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-[#0F1730] text-slate-400">or sign up with</span>
              </div>
            </div>

            <Button
              type="button" variant="outline"
              className="w-full h-11 font-semibold border-[#0A66C2]/60 bg-transparent text-white hover:bg-[#0A66C2]/15"
              onClick={async () => {
                try {
                  setLinkedinLoading(true);
                  const API_URL = process.env.REACT_APP_BACKEND_URL;
                  const res = await fetch(`${API_URL}/api/auth/linkedin`);
                  const data = await res.json();
                  if (data.auth_url) window.location.href = data.auth_url;
                } catch (e) { console.error('LinkedIn auth init failed:', e); } finally { setLinkedinLoading(false); }
              }}
              disabled={linkedinLoading || loading}
              data-testid="linkedin-register-button"
            >
              {linkedinLoading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Connecting...</>) : (<><Linkedin className="mr-2 h-5 w-5" /> Sign up with LinkedIn</>)}
            </Button>

            <Button
              type="button" variant="outline"
              className="w-full h-11 mt-3 font-semibold border-[#2A3658] bg-transparent text-slate-200 hover:bg-[#131C3A] hover:text-white"
              onClick={async () => {
                try {
                  setTwitterLoading(true);
                  const API_URL = process.env.REACT_APP_BACKEND_URL;
                  const res = await fetch(`${API_URL}/api/auth/twitter`);
                  const data = await res.json();
                  if (data.auth_url) window.location.href = data.auth_url;
                } catch (e) { console.error('X (Twitter) auth init failed:', e); } finally { setTwitterLoading(false); }
              }}
              disabled={twitterLoading || loading}
              data-testid="twitter-register-button"
            >
              {twitterLoading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Connecting...</>) : (<><Twitter className="mr-2 h-5 w-5" /> Sign up with X</>)}
            </Button>

            <Button
              type="button" variant="outline"
              className="w-full h-11 mt-3 font-semibold border-[#1877F2]/60 bg-transparent text-white hover:bg-[#1877F2]/15"
              onClick={async () => {
                try {
                  setFacebookLoading(true);
                  const API_URL = process.env.REACT_APP_BACKEND_URL;
                  const res = await fetch(`${API_URL}/api/auth/facebook`);
                  const data = await res.json();
                  if (data.auth_url) window.location.href = data.auth_url;
                } catch (e) { console.error('Facebook auth init failed:', e); } finally { setFacebookLoading(false); }
              }}
              disabled={facebookLoading || loading}
              data-testid="facebook-register-button"
            >
              {facebookLoading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Connecting...</>) : (<><Facebook className="mr-2 h-5 w-5" /> Sign up with Facebook</>)}
            </Button>

            <div className="mt-6 text-center">
              <p className="text-sm text-slate-400">
                Already have an account?{' '}
                <Link to="/login" className="font-semibold text-red-400 hover:text-red-300 transition-colors" data-testid="register-login-link">
                  Sign in
                </Link>
              </p>
            </div>

            <div className="mt-4 text-xs text-slate-500 text-center">
              By creating an account, you agree to our Terms of Service and Privacy Policy
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-8">
          <Link to="/" className="text-sm text-slate-400 hover:text-white transition-colors">
            ← Back to homepage
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
